/* eslint-disable no-param-reassign */
/* eslint-disable max-lines */
/**
 * commonly used functions
 * Add a level of indirection to lodash so we can replace
 * it if desired, while keeping function names the same
 */
// TODO: Import individual packages vs referencing all of lodash
import {
  cloneDeep,
  filter,
  debounce,
  findIndex,
  isEmpty,
  isString,
  isEqual,
  noop,
  omit,
  orderBy,
  pull,
  sumBy,
  maxBy,
  meanBy,
  sortBy,
  transform,
  isFunction,
  uniq,
  uniqBy,
  groupBy,
  property,
  get,
  set,
  range,
  parseInt,
  padStart,
  isNaN,
  values,
  throttle,
  pick,
  pickBy,
  differenceBy,
  differenceWith,
  isEqualWith,
  curry,
  isObject,
  isBoolean,
} from 'lodash-es';

type UnknownFunctionT = (...args: unknown[]) => unknown;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidKeys = string | number | symbol;

/**
 * Concatenates classNames from arguments
 * @param {...any} classnames classnames you want to join
 * @returns {string} All classnames string concatenated
 */
export const cx = (...classnames: string[]): string => classnames.reduce((result, cn) => `${result} ${cn || ''}`, '');

// if entry exists, remove it, if not, add it
const toggleInObject = (obj: Record<string, unknown>, key: string, value: unknown) =>
  obj[key] !== undefined ? omit(obj, [key]) : { ...obj, [key]: value };

const toggleInArray = (array: unknown[], value: unknown) => (array.indexOf(value) === -1 ? [value] : []);

const arrayMoveMutate = (array: unknown[], from: number, to: number) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};

const arrayMove = <T,>(array: T[], from: number, to: number): T[] => {
  array = array.slice();
  arrayMoveMutate(array, from, to);
  return array;
};

const accessorDefault = <T,>(item: T): T => item;

const addOrRemove = <T,>(array: T[], item: T, accessor = accessorDefault): T[] => {
  const index = array.findIndex((iItem) => accessor(iItem) === accessor(item));

  if (index !== -1) {
    const nextArray = cloneDeep(array);
    nextArray.splice(index, 1);
    return nextArray;
  }

  return [...array, item];
};

export function runAll(...fns: (UnknownFunctionT | UnknownFunctionT[])[]) {
  return (...args: unknown[]) =>
    fns.forEach((fn) => {
      if (Array.isArray(fn)) runAll(fn);
      if (isFunction(fn)) fn(...args);
    });
}

export function hashArray(arr: (ValidKeys | Record<string, ValidKeys>)[], fieldName?: string) {
  return arr.reduce<Record<ValidKeys, boolean>>((result, item) => {
    const newKey: ValidKeys = fieldName && typeof item === 'object' ? item[fieldName] : (item as ValidKeys);
    return {
      ...result,
      [newKey]: true,
    };
  }, {});
}

export function safeCall(fun: unknown, ...args: unknown[]) {
  if (isFunction(fun)) {
    fun(...args);
  }
}

export const removeUndefinedProperties = (obj: Record<string, unknown>) => {
  const cleanObject = { ...obj };
  Object.keys(obj).forEach((key) => cleanObject[key] === undefined && delete cleanObject[key]);
  return cleanObject;
};

export default {};
export {
  isObject,
  curry,
  arrayMove,
  uniq,
  filter,
  uniqBy,
  groupBy,
  property,
  get,
  set,
  cloneDeep,
  debounce,
  findIndex,
  isEmpty,
  isString,
  isEqual,
  maxBy,
  meanBy,
  noop,
  omit,
  orderBy,
  pull,
  sortBy,
  sumBy,
  transform,
  isFunction,
  range,
  parseInt,
  padStart,
  isNaN,
  values,
  throttle,
  toggleInObject,
  toggleInArray,
  pick,
  pickBy,
  differenceBy,
  differenceWith,
  addOrRemove,
  isEqualWith,
  isBoolean,
};
